import { TooltipIcon } from '@ipfabric/ui-components/Tooltip/TooltipIcon';
import type { settingTypes } from '@ipfabric/utils';
import { FormikUploadField } from 'components/FormikUploadField/FormikUploadField';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, type FormikProps, useFormik } from 'formik';
import type { JWTInput } from 'google-auth-library';
import { type FC, memo } from 'react';
import { validateSlug } from 'utils/formValidation/validations';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { VendorDialogContainer } from './VendorDialogContainer';
import { FormikVendorApiCommentField } from './fields/FormikVendorApiCommentField';
import { FormikVendorApiEnabledField } from './fields/FormikVendorApiEnabledField';
import { FormikVendorApiRespectSystemProxyConfigurationField } from './fields/FormikVendorApiRejectUnauthorizedField';
import { FormikVendorApiSlugField } from './fields/FormikVendorApiSlugField';
import { isVendorUpdate } from './helpers/guardTypes';
import type { BaseVendorFormValues, VendorDialogProps } from './vendorTypes';

// Use extended type for credentialsJson field like File[] | string | JWTInput & Record<string, unknown>
// because FormikUploadField produces File[] but the GCP form should send the json content
// (JWTInput & Record<string, unknown>). 'string' type is required by validation.
// 'undefined' type is sent only on edit mode when credentialsJson is empty not to rewrite the key
// stored on the server by the empty string.
export type GCPFormValues = BaseVendorFormValues<
  Omit<settingTypes.GcpApiSettings, 'credentialsJson'>
> & {
  credentialsJson:
    | File[]
    | string
    | (JWTInput & Record<string, unknown>)
    | undefined;
};

const isJWTInput = (value: unknown): value is JWTInput => {
  return value !== null && !Array.isArray(value) && typeof value === 'object';
};

const isCredentialsJsonEmpty = (values: GCPFormValues): boolean => {
  return (
    !values.credentialsJson ||
    (Array.isArray(values.credentialsJson) &&
      values.credentialsJson.length === 0) ||
    (isJWTInput(values.credentialsJson) &&
      Object.keys(values.credentialsJson).length === 0)
  );
};

type Props = VendorDialogProps<
  GCPFormValues,
  settingTypes.GcpApiSettings,
  settingTypes.UpdateGcpApiSettings
>;

const MAX_SIZE_IN_MB = 1;

const BareGoogleCloudPlatformForm: FC<Props> = (props) => {
  const readFile = async (
    loadedFile: File,
  ): Promise<JWTInput & Record<string, unknown>> => {
    try {
      const fileContent = await loadedFile.text();
      return JSON.parse(fileContent);
    } catch (error) {
      throw new Error(
        `Error occurred reading file: ${loadedFile.name}. \n${error}`,
      );
    }
  };

  const editMode = props.dialogType === 'edit';

  const msg = useLocale();
  const { required } = msg.discoverySettings.vendorsApi.validations;

  const formik = useFormik<GCPFormValues>({
    initialValues: props.initialValues,
    validate: (values) => {
      const errors: FormikErrors<GCPFormValues> = {};
      if (
        !editMode &&
        (!values.credentialsJson || isCredentialsJsonEmpty(values))
      ) {
        errors.credentialsJson = required;
      }

      validateSlug(values, errors, msg);

      return errors;
    },
    onSubmit: makeFormikOnSubmit(async (values: GCPFormValues) => {
      const credentialsJson =
        Array.isArray(values.credentialsJson) &&
        values.credentialsJson.length > 0
          ? // Fixed noUncheckedIndexedAccess NIM-19408
            await readFile(values.credentialsJson[0]!)
          : isJWTInput(props.initialValues.credentialsJson)
            ? props.initialValues.credentialsJson
            : undefined;

      if (isVendorUpdate(values) && props.dialogType === 'edit') {
        props.onSubmit({
          ...values,
          id: values.id,
          credentialsJson,
        });
      } else if (props.dialogType === 'add' && credentialsJson !== undefined) {
        props.onSubmit({
          ...values,
          credentialsJson,
        });
      }
    }),
  });

  return (
    <VendorDialogContainer
      formik={formik as FormikProps<GCPFormValues>}
      dialogType={props.dialogType}
      {...props.vendorDialogContainerPassthruProps}
    >
      <FormikVendorApiEnabledField formik={formik} />
      <FormikUploadField
        formik={formik}
        name='credentialsJson'
        accept='.json'
        confirmRepeatedUploadHeaderMsg={
          msg.discoverySettings.vendorsApi.gcpForm.confirmDialogHeaderMsg
        }
        maxSizeInMB={MAX_SIZE_IN_MB}
        label={
          <>
            {msg.discoverySettings.vendorsApi.gcpForm.credentialsJson}
            <TooltipIcon
              content={
                msg.discoverySettings.vendorsApi.gcpForm.credentialsJsonTooltip
              }
              iconProps={{
                iconStyle: 'solid',
                inline: true,
              }}
              size='sm'
              className='ml-2'
            />
          </>
        }
      />
      <FormikVendorApiSlugField formik={formik} />
      <FormikVendorApiCommentField formik={formik} />
      <FormikVendorApiRespectSystemProxyConfigurationField formik={formik} />
    </VendorDialogContainer>
  );
};

export const GoogleCloudPlatformForm = memo(BareGoogleCloudPlatformForm);
